import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FAQ() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [openKeys, setOpenKeys] = useState(new Set());

  useEffect(() => {
    fetch(`${API}/faq`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ categories: [] }));
  }, []);

  const filtered = useMemo(() => {
    if (!data?.categories) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.categories;
    return data.categories
      .map((cat) => ({
        ...cat,
        questions: cat.questions.filter(
          (item) =>
            item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.questions.length > 0);
  }, [data, query]);

  const toggle = (key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // FAQ JSON-LD for Google rich results
  const faqSchema = useMemo(() => {
    if (!data?.categories) return null;
    const mainEntity = data.categories
      .flatMap((c) => c.questions)
      .map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      }));
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity,
    };
  }, [data]);

  const totalCount = data?.categories
    ? data.categories.reduce((n, c) => n + c.questions.length, 0)
    : 0;

  return (
    <div className="min-h-screen bg-obsidian-900 text-bone-200">
      <Helmet>
        <title>FAQ · Umbra Shadow Speak Journal</title>
        <meta name="description" content="Answers to common questions about Umbra Shadow Speak Journal — voice journaling, subscriptions, ebooks, keepsake books, accessibility, and more." />
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Helmet>

      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
        <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-6" data-testid="faq-eyebrow">
          Help · {totalCount} answers
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-[1.1] text-bone-100 mb-4" data-testid="faq-title">
          Frequently asked questions
        </h1>
        <p className="font-serif italic text-lg text-bone-400 mb-10">
          Everything about Umbra — how it works, what it costs, what happens to your data.
        </p>

        {/* Search */}
        <div className="relative mb-12" data-testid="faq-search-wrap">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-bone-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions…"
            data-testid="faq-search"
            className="pl-11 py-6 bg-obsidian-800 border-obsidian-600 rounded-none font-sans text-base text-bone-100 placeholder:text-bone-500 focus:border-terracotta"
            aria-label="Search FAQ"
          />
          {query && (
            <p className="mt-2 font-sans text-xs uppercase tracking-[0.2em] text-bone-500" data-testid="faq-search-count">
              {filtered.reduce((n, c) => n + c.questions.length, 0)} matches for "{query}"
            </p>
          )}
        </div>

        {/* Category jump nav */}
        {!query && data?.categories && (
          <nav aria-label="FAQ categories" className="mb-10 flex flex-wrap gap-3" data-testid="faq-category-nav">
            {data.categories.map((cat) => (
              <a
                key={cat.slug}
                href={`#cat-${cat.slug}`}
                data-testid={`faq-cat-link-${cat.slug}`}
                className="font-sans uppercase tracking-[0.18em] text-[0.7rem] border border-obsidian-600 px-3 py-2 text-bone-300 hover:border-terracotta hover:text-terracotta transition-colors"
              >
                {cat.category}
              </a>
            ))}
          </nav>
        )}

        {/* Categorized Q&A */}
        <div className="space-y-16" data-testid="faq-list">
          {filtered.map((cat) => (
            <section key={cat.slug} id={`cat-${cat.slug}`} data-testid={`faq-cat-${cat.slug}`}>
              <h2 className="font-serif text-2xl text-bone-100 mb-5 border-b border-obsidian-600 pb-3">
                {cat.category}
              </h2>
              <ul className="divide-y divide-obsidian-600/60">
                {cat.questions.map((item, i) => {
                  const key = `${cat.slug}-${i}`;
                  const isOpen = openKeys.has(key) || Boolean(query);
                  return (
                    <li key={key} data-testid={`faq-item-${key}`}>
                      <button
                        type="button"
                        onClick={() => toggle(key)}
                        aria-expanded={isOpen}
                        aria-controls={`answer-${key}`}
                        data-testid={`faq-question-${key}`}
                        className="w-full flex items-start justify-between gap-4 py-5 text-left group"
                      >
                        <span className="font-serif text-lg text-bone-100 group-hover:text-terracotta transition-colors">
                          {item.q}
                        </span>
                        <ChevronDown
                          className={`h-4 w-4 mt-1.5 flex-shrink-0 text-bone-500 transition-transform ${
                            isOpen ? "rotate-180 text-terracotta" : ""
                          }`}
                        />
                      </button>
                      {isOpen && (
                        <div
                          id={`answer-${key}`}
                          data-testid={`faq-answer-${key}`}
                          className="pb-5 pr-8"
                        >
                          <p className="font-sans text-base text-bone-300 leading-relaxed">
                            {item.a}
                          </p>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
          {query && filtered.length === 0 && (
            <div className="text-center py-16" data-testid="faq-empty">
              <p className="font-serif italic text-lg text-bone-400 mb-2">
                No answers match "{query}".
              </p>
              <p className="font-sans text-sm text-bone-500">
                Try a different search, or email{" "}
                <a href="mailto:hello@shadowspeakjournal.com" className="underline text-terracotta">
                  hello@shadowspeakjournal.com
                </a>
                .
              </p>
            </div>
          )}
        </div>

        <div className="mt-20 border-t border-obsidian-600 pt-8 flex flex-wrap gap-3 sm:gap-6">
          <Link to="/terms" className="inline-flex items-center min-h-[44px] px-3 sm:px-1 sm:min-h-0 py-2 sm:py-1 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta" data-testid="faq-terms-link">
            Terms of Service →
          </Link>
          <Link to="/privacy" className="inline-flex items-center min-h-[44px] px-3 sm:px-1 sm:min-h-0 py-2 sm:py-1 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta" data-testid="faq-privacy-link">
            Privacy Policy →
          </Link>
          <Link to="/" className="inline-flex items-center min-h-[44px] px-3 sm:px-1 sm:min-h-0 py-2 sm:py-1 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta" data-testid="faq-home-link">
            ← Back to the workbook
          </Link>
        </div>
      </div>
    </div>
  );
}
